<template>
  <div class="relative z-[200]">
    <Overlay
      :visible="isDateRequestSidebarOpen"
      @click="handleDateRequestSidebarClose"
    />
    <Drawer
      ref="dateRequestSidebarRef"
      class="w-full sm:w-[600px] p-4 pt-0 pb-32 flex flex-col !bg-gray-50"
      :class="{
        'bg-contain bg-no-repeat bg-bottom': privateDateRequestCompleted,
      }"
      :style="
        privateDateRequestCompleted && {
          backgroundImage:
            'url(\'/_nuxt3-static/events/date-request-success.svg\')',
        }
      "
      :is-open="isDateRequestSidebarOpen"
      @click:close="handleDateRequestSidebarClose"
    >
      <template #header>
        <div
          v-if="!dateRequestCompleted && !privateDateRequestCompleted"
          class="title flex items-center"
        >
          <button
            v-if="showMatchingDatesLayer"
            class="mr-3 p-1 hover:bg-gray-200 rounded-full transition-colors"
            @click="handleBackToForm"
          >
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span>
            {{
              showMatchingDatesLayer
                ? t('dateRequest.availableDates')
                : t('dateRequest.sidebarTitle')
            }}
          </span>
        </div>
      </template>
      <div class="flex flex-col justify-between flex-1">
        <DateRequestForm
          v-if="
            isDateRequestSidebarOpen &&
            !dateRequestCompleted &&
            !privateDateRequestCompleted
          "
          ref="dateRequestFormRef"
          @on-complete-request="handleDateRequestComplete"
          @on-complete-private-request="privateDateRequestCompleted = true"
          @on-close-date-request="handleDateRequestSidebarClose"
        />
        <DateRequestSuccess
          v-if="dateRequestCompleted"
          :date-request-id="dateRequestId"
          @on-close-date-request="dateRequestCompleted = false"
        />
        <DateRequestSuccessAndShare
          v-if="privateDateRequestCompleted"
          @on-share-click="handleDateRequestSidebarClose"
        />
      </div>
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import {
  popMobileDrawerHistory,
  pushMobileDrawerHistory,
  registerMobileDrawerCloseHandler,
  registerMobileDrawerIsOpenHandler,
  registerMobileDrawerOpenHandler,
} from '~/composables/useDrawerBackNavigation'

const dateRequestSidebarRef = ref(null)
const dateRequestFormRef = ref(null)

const {
  toggleDateRequestSidebar,
  openDateRequestSidebar,
  closeDateRequestSidebar,
  isDateRequestSidebarOpen,
} = useUiState()
const { t } = useI18n()
const dateRequestCompleted = ref(false)
const privateDateRequestCompleted = ref(false)
const dateRequestId = ref<string | undefined>()

const showMatchingDatesLayer = computed(() => {
  return (dateRequestFormRef.value as any)?.showMatchingDatesLayer || false
})

const closeMatchingDatesLayer = () => {
  ;(
    dateRequestFormRef.value as { closeMatchingDatesLayer?: () => void } | null
  )?.closeMatchingDatesLayer?.()
}

const openMatchingDatesLayer = () => {
  ;(
    dateRequestFormRef.value as { openMatchingDatesLayer?: () => void } | null
  )?.openMatchingDatesLayer?.()
}

const handleDateRequestComplete = (id?: string) => {
  dateRequestId.value = id
  dateRequestCompleted.value = true
}

const handleDateRequestSidebarClose = () => {
  closeDateRequestSidebar()
  resetDateRequestEventRequestForms()
}

const handleBackToForm = () => {
  closeMatchingDatesLayer()
  popMobileDrawerHistory('date-request-matching-dates')
}

const handleDateRequestSidebarCloseFromHistory = () => {
  closeDateRequestSidebar({ skipHistory: true })
}

onMounted(() => {
  registerMobileDrawerOpenHandler('date-request', openDateRequestSidebar)
  registerMobileDrawerCloseHandler(
    'date-request',
    handleDateRequestSidebarCloseFromHistory
  )
  registerMobileDrawerIsOpenHandler(
    'date-request',
    () => isDateRequestSidebarOpen.value
  )
  registerMobileDrawerOpenHandler(
    'date-request-matching-dates',
    openMatchingDatesLayer
  )
  registerMobileDrawerCloseHandler('date-request-matching-dates', () => {
    closeMatchingDatesLayer()
  })
  registerMobileDrawerIsOpenHandler(
    'date-request-matching-dates',
    () => showMatchingDatesLayer.value
  )
})

watch(showMatchingDatesLayer, (isOpen) => {
  if (isOpen && isDateRequestSidebarOpen.value) {
    pushMobileDrawerHistory('date-request-matching-dates')
  }
})

const resetDateRequestEventRequestForms = () => {
  privateDateRequestCompleted.value = false
  dateRequestCompleted.value = false
  dateRequestId.value = undefined
}

const scrollDateRequestDrawerToTop = () => {
  nextTick(() => {
    const root = (dateRequestSidebarRef.value as { $el?: HTMLElement } | null)
      ?.$el
    if (root && typeof root.scrollTop === 'number') {
      root.scrollTop = 0
    }
  })
}

provide('resetDateRequestEventRequestForms', resetDateRequestEventRequestForms)
provide('scrollDateRequestDrawerToTop', scrollDateRequestDrawerToTop)

watch(
  [dateRequestCompleted, privateDateRequestCompleted],
  ([completed, privateCompleted]) => {
    if (completed || privateCompleted) {
      scrollDateRequestDrawerToTop()
    }
  }
)

defineExpose({ toggleDateRequestSidebar })
</script>
<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>

<style lang="postcss" scoped>
.title {
  @apply h-14 flex items-center text-lg font-medium text-[#666971] mb-[-1rem];
}
</style>
